import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';
import './PainelGestor.css';

const DEFAULT_SYSTEM_PROMPT = `Voce e um assistente virtual do Laboratorio Santa Rita. Ajude pacientes com informacoes sobre resultados de exames, status de protocolos e informacoes gerais do laboratorio.

Regras:
- Seja educado, objetivo e profissional
- Responda sempre em portugues brasileiro
- Use emojis com moderacao
- Se nao souber algo, indique que o paciente deve entrar em contato com o laboratorio diretamente
- Nunca invente informacoes sobre resultados de exames
- Use as funcoes disponiveis para buscar dados reais do sistema`;

const DEFAULT_KNOWLEDGE_BASE = [
  {
    categoria: 'Contato e Atendimento',
    conteudo: 'Telefone único (também WhatsApp): (37) 3341-1460\nHorário de atendimento: Segunda a sexta das 06:30 às 17:30 · Sábados das 07:00 às 11:00\nDomingos e feriados: fechado.',
  },
  {
    categoria: 'Unidades e Endereços',
    conteudo: 'Matriz Itapecerica: Av. Ribeiro Pena, 301-A, Centro - Itapecerica/MG - CEP 35.550-000 (CRF/MG 42.189 · CNES 0483907)\nFilial Pedra do Indaiá: Praça Gabriel Resende Passos, 60, Centro - Pedra do Indaiá/MG - CEP 35.565-000 (CRF/MG 47.706 · CNES 5810086)\nFilial Camacho: Rua Arlindo Isaias, 07, Centro - Camacho/MG - CEP 35.555-000 (CRF/MG 44.018 · CNES 3352412)',
  },
  {
    categoria: 'Resultados Online',
    conteudo: 'Portal de resultados: http://app.lifesys.com.br/laudos\nApp Allis: disponível na Play Store e App Store - permite consultar resultados pelo celular\nO código e senha de acesso são impressos na requisição do paciente (parte direita superior) e também enviados automaticamente por mensagem do WhatsApp quando o exame fica pronto.',
  },
  {
    categoria: 'Coleta Domiciliar',
    conteudo: 'O laboratório oferece coleta domiciliar para Itapecerica, Pedra do Indaiá, Camacho e região. Para agendar é necessário: nome completo, endereço completo, data e horário preferidos, lista de exames a coletar (foto do pedido médico facilita). A confirmação do agendamento é feita pela equipe após análise da agenda.',
  },
  {
    categoria: 'Preparo de Exames (Geral)',
    conteudo: 'Jejum obrigatório de 8 a 12 horas: glicose, colesterol total e frações, triglicérides, ácido úrico, ureia, perfil lipídico completo, insulina.\nPode beber água em pequena quantidade durante o jejum. Evitar atividade física intensa antes da coleta.\nNão precisam de jejum: hemograma, TSH, eletrólitos, vitamina D, ferro/ferritina (recomendado evitar suplementos de ferro 24h antes), maioria dos exames hormonais.\nColeta de urina rotina: primeira urina da manhã, jato médio, em frasco fornecido pelo laboratório ou comprado em farmácia.\nColeta de fezes: amostra do dia, em frasco fornecido pelo laboratório.\nPara confirmar preparo de exame específico, usar a ferramenta buscar_exame.',
  },
  {
    categoria: 'Marcação de Exames Presenciais',
    conteudo: 'Para exames coletados na unidade NÃO há agendamento prévio — atendimento é por ordem de chegada dentro do horário de funcionamento.\nO paciente deve comparecer com o pedido médico em mãos, documento com foto e carteirinha do convênio (se houver).\nColeta domiciliar é a única modalidade que requer agendamento (ver categoria "Coleta Domiciliar").',
  },
  {
    categoria: 'Convênios Aceitos',
    conteudo: 'Particular: sempre aceito.\nConvênios atendidos: Unimed, IPSEMG, GEAP, Bradesco Saúde, SUS (parceria via Prefeitura — somente exames listados no contrato).\nOutros convênios: para confirmar cobertura de um plano específico, usar a ferramenta consultar_preco_exame informando o convênio. Se a tool não retornar valor para aquele exame+convênio, orientar o paciente a ligar para confirmar.',
  },
  {
    categoria: 'Validade do Pedido Médico',
    conteudo: 'O pedido médico fica disponível para realização dos exames por 30 dias a partir da data de emissão pelo médico.\nApós esse prazo, é necessário um novo pedido com o médico solicitante.\nO laboratório guarda os pedidos coletados (já realizados) pelo período de 60 dias.',
  },
  {
    categoria: 'Segunda Via de Resultados',
    conteudo: 'Resultados ficam disponíveis online no portal Autolac/Allis pelo prazo de 60 dias após a liberação.\nPara resultados mais antigos (acima de 60 dias): paciente deve comparecer à unidade onde realizou o exame com documento de identidade.\nReenvio por WhatsApp ou e-mail: somente para protocolos dos últimos 30 dias e mediante confirmação de identidade.\nRecibos/notas fiscais: emissão e segunda via apenas na recepção da unidade.',
  },
  {
    categoria: 'Recoleta de Exames',
    conteudo: 'Recoleta acontece quando o material foi insuficiente, hemolisou (sangue alterado) ou houve problema no processamento pelo laboratório de apoio.\nProcedimento: o laboratório entra em contato (ligação ou WhatsApp) pedindo ao paciente para comparecer novamente — sem custo adicional.\nRecoleta vale para o mesmo pedido médico original — não precisa de novo pedido.',
  },
];

function WhatsAppAgente() {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Config state
  const [config, setConfig] = useState({
    ai_enabled: '0',
    ai_nome_agente: '',
    openrouter_api_key: '',
    ai_model: 'google/gemini-2.0-flash-001',
    ai_temperature: '0.7',
    ai_max_tokens: '1000',
    ai_system_prompt: DEFAULT_SYSTEM_PROMPT,
    ai_horario_inicio: '07:00',
    ai_horario_fim: '22:00',
    ai_mensagem_fora_horario: 'Nosso atendimento automatico funciona das {hora_inicio} as {hora_fim}. Por favor, envie sua mensagem nesse horario.',
    ai_max_turns_per_conversation: '20',
    ai_enabled_tools: '',
    ai_knowledge_base: '',
    ai_mining_model: 'nvidia/nemotron-3-nano-30b-a3b',
    ai_validator_enabled: '0',
    ai_validator_model: '',
    ai_followup_enabled: '1',
    ai_followup_timeout_min: '15',
    ai_close_timeout_min: '15',
    ai_msg_abertura: 'Atendimento iniciado!\nProtocolo: *{protocolo}*\nComo posso ajudar?',
    ai_msg_followup: 'Ola! Notamos que voce nao respondeu. Ainda precisa de ajuda? Estamos aqui para atender!',
    ai_msg_fechamento_inatividade: 'Encerramos este atendimento por inatividade.\nProtocolo: *{protocolo}*\nSe precisar de ajuda, basta enviar uma nova mensagem!',
    ai_msg_fechamento_operador: 'Atendimento encerrado pelo operador.\nProtocolo: *{protocolo}*\nSe precisar de ajuda, basta enviar uma nova mensagem!',
    ai_buttons_enabled: '0',
    ai_buttons_model: '',
    ai_transcription_enabled: '0',
    ai_transcription_model: '',
    ai_pdf_usar_interno: '0',
  });
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [editingApiKey, setEditingApiKey] = useState(false);
  const [availableTools, setAvailableTools] = useState([]);
  const [testingConnection, setTestingConnection] = useState(false);

  // Models state
  const [allModels, setAllModels] = useState([]);
  const [modelsFilter, setModelsFilter] = useState('');
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsSortBy, setModelsSortBy] = useState('provider');
  const [modelsSortDir, setModelsSortDir] = useState('asc');
  const [showModelBrowser, setShowModelBrowser] = useState(null); // null | 'ai_model' | 'ai_mining_model' | 'ai_validator_model'

  // Webhooks state
  const [webhooks, setWebhooks] = useState([]);
  const [webhooksTotal, setWebhooksTotal] = useState(0);
  const [webhookFilters, setWebhookFilters] = useState({ event_type: '', from_phone: '' });
  const [expandedWebhook, setExpandedWebhook] = useState(null);
  const [webhooksLoading, setWebhooksLoading] = useState(false);

  // Test chat state
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState('');
  const [testSending, setTestSending] = useState(false);
  const testChatRef = useRef(null);

  // Mining state
  const [miningStats, setMiningStats] = useState(null);
  const [mining, setMining] = useState(false);
  const [miningProgress, setMiningProgress] = useState(0);
  const [miningLog, setMiningLog] = useState([]);
  const [miningResults, setMiningResults] = useState(null);
  const [miningBatchSize, setMiningBatchSize] = useState(20);
  const [miningMaxConversas, setMiningMaxConversas] = useState(200);
  const [miningImporting, setMiningImporting] = useState(false);
  const [miningHistory, setMiningHistory] = useState(null);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  // ==================== CONFIG ====================

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/whatsapp/ai/config`);
      const data = res.data;

      setConfig(prev => ({
        ...prev,
        ai_enabled: data.ai_enabled || '0',
        ai_nome_agente: data.ai_nome_agente || '',
        ai_model: data.ai_model || prev.ai_model,
        ai_temperature: data.ai_temperature || prev.ai_temperature,
        ai_max_tokens: data.ai_max_tokens || prev.ai_max_tokens,
        ai_system_prompt: data.ai_system_prompt || prev.ai_system_prompt,
        ai_horario_inicio: data.ai_horario_inicio || prev.ai_horario_inicio,
        ai_horario_fim: data.ai_horario_fim || prev.ai_horario_fim,
        ai_mensagem_fora_horario: data.ai_mensagem_fora_horario || prev.ai_mensagem_fora_horario,
        ai_max_turns_per_conversation: data.ai_max_turns_per_conversation || prev.ai_max_turns_per_conversation,
        ai_enabled_tools: data.ai_enabled_tools || '',
        ai_knowledge_base: data.ai_knowledge_base || '',
        ai_mining_model: data.ai_mining_model || prev.ai_mining_model,
        ai_validator_enabled: data.ai_validator_enabled || prev.ai_validator_enabled,
        ai_validator_model: data.ai_validator_model || prev.ai_validator_model,
        ai_followup_enabled: data.ai_followup_enabled || prev.ai_followup_enabled,
        ai_followup_timeout_min: data.ai_followup_timeout_min || prev.ai_followup_timeout_min,
        ai_close_timeout_min: data.ai_close_timeout_min || prev.ai_close_timeout_min,
        ai_msg_abertura: data.ai_msg_abertura || prev.ai_msg_abertura,
        ai_msg_followup: data.ai_msg_followup || prev.ai_msg_followup,
        ai_msg_fechamento_inatividade: data.ai_msg_fechamento_inatividade || prev.ai_msg_fechamento_inatividade,
        ai_msg_fechamento_operador: data.ai_msg_fechamento_operador || prev.ai_msg_fechamento_operador,
        ai_buttons_enabled: data.ai_buttons_enabled || '0',
        ai_buttons_model: data.ai_buttons_model || '',
        ai_transcription_enabled: data.ai_transcription_enabled || '0',
        ai_transcription_model: data.ai_transcription_model || '',
        ai_pdf_usar_interno: data.ai_pdf_usar_interno || '0',
      }));

      // Parse knowledge base
      if (data.ai_knowledge_base) {
        try {
          const kb = JSON.parse(data.ai_knowledge_base);
          setKnowledgeBase(kb);
        } catch (e) {
          setKnowledgeBase([]);
        }
      } else {
        setKnowledgeBase([]);
      }
      setHasApiKey(data.has_api_key || false);
      setApiKeyMasked(data.openrouter_api_key_masked || '');
      setAvailableTools(data.available_tools || []);
    } catch (err) {
      console.error('Erro ao buscar config:', err);
      showMessage('Erro ao carregar configuracao', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const toSave = { ...config };
      if (!editingApiKey) delete toSave.openrouter_api_key;
      toSave.ai_knowledge_base = JSON.stringify(knowledgeBase);
      await axios.put(`${API_URL}/whatsapp/ai/config`, toSave);
      showMessage('Configuracao salva com sucesso!');
      setEditingApiKey(false);
      fetchConfig();
    } catch (err) {
      console.error('Erro ao salvar:', err);
      showMessage('Erro ao salvar configuracao', 'error');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await axios.post(`${API_URL}/whatsapp/ai/test-connection`);
      if (res.data.success) {
        showMessage(`Conexao OK! Modelo: ${res.data.model}. Resposta: "${res.data.message}"`);
      } else {
        showMessage('Falha na conexao com OpenRouter', 'error');
      }
    } catch (err) {
      showMessage('Erro ao testar conexao: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  // ==================== MODELS ====================

  const fetchModels = useCallback(async () => {
    setModelsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/whatsapp/ai/models`);
      setAllModels(res.data || []);
    } catch (err) {
      console.error('Erro ao buscar modelos:', err);
      showMessage('Erro ao buscar modelos OpenRouter', 'error');
    } finally {
      setModelsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'config' && allModels.length === 0) fetchModels();
  }, [activeTab, allModels.length, fetchModels]);

  const filteredModels = allModels.filter(m => {
    if (!modelsFilter) return true;
    const q = modelsFilter.toLowerCase();
    return m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q);
  });

  const sortedModels = [...filteredModels].sort((a, b) => {
    let va, vb;
    switch (modelsSortBy) {
      case 'provider': va = a.provider; vb = b.provider; break;
      case 'name': va = a.name; vb = b.name; break;
      case 'context': va = a.context_length; vb = b.context_length; return modelsSortDir === 'asc' ? va - vb : vb - va;
      case 'input': va = a.pricing.prompt_per_million; vb = b.pricing.prompt_per_million; return modelsSortDir === 'asc' ? va - vb : vb - va;
      case 'output': va = a.pricing.completion_per_million; vb = b.pricing.completion_per_million; return modelsSortDir === 'asc' ? va - vb : vb - va;
      case 'created': va = a.created || 0; vb = b.created || 0; return modelsSortDir === 'asc' ? va - vb : vb - va;
      default: va = a.provider; vb = b.provider;
    }
    if (typeof va === 'string') return modelsSortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    return 0;
  });

  const handleModelSort = (col) => {
    if (modelsSortBy === col) {
      setModelsSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setModelsSortBy(col);
      setModelsSortDir('asc');
    }
  };

  const sortIcon = (col) => modelsSortBy === col ? (modelsSortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  // ==================== WEBHOOKS ====================

  const fetchWebhooks = useCallback(async () => {
    setWebhooksLoading(true);
    try {
      const params = new URLSearchParams();
      if (webhookFilters.event_type) params.set('event_type', webhookFilters.event_type);
      if (webhookFilters.from_phone) params.set('from_phone', webhookFilters.from_phone);
      params.set('limit', '50');
      const res = await axios.get(`${API_URL}/whatsapp/webhooks?${params.toString()}`);
      setWebhooks(res.data.webhooks || []);
      setWebhooksTotal(res.data.total || 0);
    } catch (err) {
      console.error('Erro ao buscar webhooks:', err);
    } finally {
      setWebhooksLoading(false);
    }
  }, [webhookFilters]);

  useEffect(() => {
    if (activeTab === 'webhooks') fetchWebhooks();
  }, [activeTab, fetchWebhooks]);

  // Auto-refresh webhooks cada 15s
  useEffect(() => {
    if (activeTab !== 'webhooks') return;
    const interval = setInterval(fetchWebhooks, 15000);
    return () => clearInterval(interval);
  }, [activeTab, fetchWebhooks]);

  // ==================== TEST CHAT ====================

  const sendTestMessage = async () => {
    if (!testInput.trim() || testSending) return;
    const userMsg = testInput.trim();
    setTestInput('');
    setTestMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setTestSending(true);
    try {
      const res = await axios.post(`${API_URL}/whatsapp/ai/test-chat`, { message: userMsg });
      setTestMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setTestMessages(prev => [...prev, { role: 'error', content: 'Erro: ' + (err.response?.data?.error || err.message) }]);
    } finally {
      setTestSending(false);
    }
  };

  useEffect(() => {
    if (testChatRef.current) {
      testChatRef.current.scrollTop = testChatRef.current.scrollHeight;
    }
  }, [testMessages]);

  // ==================== TOOLS ====================

  const enabledToolsList = config.ai_enabled_tools ? config.ai_enabled_tools.split(',').map(t => t.trim()).filter(Boolean) : availableTools.map(t => t.name);

  const toggleTool = (toolName) => {
    const current = new Set(enabledToolsList);
    if (current.has(toolName)) {
      current.delete(toolName);
    } else {
      current.add(toolName);
    }
    setConfig(prev => ({ ...prev, ai_enabled_tools: Array.from(current).join(',') }));
  };

  // ==================== MINING ====================

  const fetchMiningStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/whatsapp/ai/kb-mining/stats`);
      setMiningStats(res.data);
    } catch (err) {
      console.error('Erro ao buscar stats mining:', err);
    }
  }, []);

  const fetchMiningHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/whatsapp/ai/kb-mining/history`);
      setMiningHistory(res.data);
    } catch (err) {
      console.error('Erro ao buscar history mining:', err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'mineracao') {
      fetchMiningStats();
      fetchMiningHistory();
    }
  }, [activeTab, fetchMiningStats, fetchMiningHistory]);

  const startMining = async () => {
    setMining(true);
    setMiningProgress(0);
    setMiningLog([]);
    setMiningResults(null);

    const totalBatches = Math.ceil(miningMaxConversas / miningBatchSize);
    const accumulated = {};
    let completed = 0;
    let errors = 0;

    setMiningLog([{ type: 'info', text: `Enviando ${totalBatches} lotes em paralelo (${miningBatchSize} conversas cada)...` }]);

    // Criar todas as promises com offsets diferentes
    const promises = Array.from({ length: totalBatches }, (_, i) => {
      const offset = i * miningBatchSize;
      return axios.post(`${API_URL}/whatsapp/ai/kb-mining/analyze`, {
        batch_size: miningBatchSize,
        offset,
      }).then(res => {
        completed++;
        const { results, batch_conversations, usage } = res.data;

        if (results && !results.parse_error) {
          for (const [key, items] of Object.entries(results)) {
            if (!Array.isArray(items) || items.length === 0) continue;
            if (!accumulated[key]) accumulated[key] = [];
            accumulated[key].push(...items);
          }
        }

        const pct = Math.round((completed / totalBatches) * 100);
        setMiningProgress(pct);

        const tokenInfo = usage ? ` | tokens: ${usage.total_tokens || 0}` : '';
        const status = !results ? 'info' : results.parse_error ? 'warn' : 'success';
        setMiningLog(prev => [...prev, {
          type: status,
          text: `Lote ${i + 1}: ${batch_conversations || 0} conversas${tokenInfo} (${completed}/${totalBatches})`,
        }]);

        return { ok: true, empty: !results || batch_conversations === 0 };
      }).catch(err => {
        completed++;
        errors++;
        setMiningProgress(Math.round((completed / totalBatches) * 100));
        setMiningLog(prev => [...prev, {
          type: 'error',
          text: `Lote ${i + 1}: ${err.response?.data?.error || err.message}`,
        }]);
        return { ok: false };
      });
    });

    await Promise.all(promises);

    const successCount = totalBatches - errors;
    setMiningLog(prev => [...prev, {
      type: errors === totalBatches ? 'error' : 'done',
      text: `Concluido! ${successCount} lotes processados${errors > 0 ? `, ${errors} com erro` : ''}.`,
    }]);

    setMiningResults(accumulated);
    setMining(false);
    fetchMiningStats();
    fetchMiningHistory();
  };

  const loadSavedResults = async () => {
    try {
      const res = await axios.get(`${API_URL}/whatsapp/ai/kb-mining/results`);
      if (res.data.results && Object.keys(res.data.results).length > 0) {
        setMiningResults(res.data.results);
        showMessage(`${res.data.total_batches} lotes carregados do historico`);
      } else {
        showMessage('Nenhum resultado salvo encontrado', 'error');
      }
    } catch (err) {
      showMessage('Erro ao carregar resultados: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const importResults = async () => {
    if (!miningResults) return;
    setMiningImporting(true);
    try {
      await axios.post(`${API_URL}/whatsapp/ai/kb-mining/import`, { results: miningResults });
      showMessage('Base de Conhecimento atualizada com sucesso!');
      fetchConfig();
    } catch (err) {
      showMessage('Erro ao importar: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setMiningImporting(false);
    }
  };

  const resetMiningHistory = async (batchId) => {
    try {
      const url = batchId
        ? `${API_URL}/whatsapp/ai/kb-mining/history?batch_id=${batchId}`
        : `${API_URL}/whatsapp/ai/kb-mining/history`;
      await axios.delete(url);
      showMessage(batchId ? 'Lote removido do historico' : 'Historico de mineracao limpo');
      fetchMiningStats();
      fetchMiningHistory();
    } catch (err) {
      showMessage('Erro: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const MINING_CATEGORY_LABELS = {
    horarios: 'Horarios de Funcionamento',
    enderecos: 'Enderecos e Unidades',
    servicos: 'Servicos e Exames',
    convenios: 'Convenios Aceitos',
    documentos: 'Documentos Necessarios',
    precos: 'Precos e Pagamento',
    contato: 'Contato',
    procedimentos: 'Procedimentos e Atendimento',
    perguntas_frequentes: 'Perguntas Frequentes',
  };

  // ==================== HELPERS ====================

  const formatPrice = (price) => {
    if (price === 0) return 'Gratis';
    if (price < 0.01) return '<$0.01';
    if (price < 1) return `$${price.toFixed(3)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatContext = (len) => {
    if (!len) return '-';
    if (len >= 1000000) return `${(len / 1000000).toFixed(1)}M`;
    if (len >= 1000) return `${(len / 1000).toFixed(0)}K`;
    return String(len);
  };

  const tabs = [
    { id: 'config', label: 'Configuracao' },
    { id: 'tools', label: 'Funcoes/Tools' },
    { id: 'webhooks', label: 'Webhooks' },
    { id: 'teste', label: 'Teste' },
    { id: 'mineracao', label: 'Mineracao IA' },
  ];

  // Find selected model info
  const selectedModel = allModels.find(m => m.id === config.ai_model);

  // Inject responsive CSS
  useEffect(() => {
    const styleId = 'whatsapp-agente-responsive';
    if (document.getElementById(styleId)) return;
    const styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.textContent = `
      @media (max-width: 768px) {
        .wa-agente-tabs {
          overflow-x: auto !important;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .wa-agente-tabs::-webkit-scrollbar {
          display: none;
        }
        .wa-agente-tabs button {
          padding: 8px 12px !important;
          font-size: 12px !important;
          white-space: nowrap !important;
          flex-shrink: 0 !important;
        }
        .wa-agente-grid-2col {
          grid-template-columns: 1fr !important;
        }
        .wa-agente-model-browser-header {
          flex-wrap: wrap !important;
        }
        .wa-agente-model-browser-header input {
          width: 100% !important;
          order: 10 !important;
        }
        .wa-agente-model-table th:nth-child(3),
        .wa-agente-model-table th:nth-child(6),
        .wa-agente-model-table td:nth-child(3),
        .wa-agente-model-table td:nth-child(6) {
          display: none !important;
        }
        .wa-agente-kb-header {
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 8px !important;
        }
        .wa-agente-tools-grid {
          grid-template-columns: 1fr !important;
        }
        .wa-agente-webhooks-table {
          overflow-x: auto !important;
          -webkit-overflow-scrolling: touch;
        }
        .wa-agente-mining-table {
          overflow-x: auto !important;
          -webkit-overflow-scrolling: touch;
        }
      }
    `;
    document.head.appendChild(styleEl);
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  const headerStyles = {
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    pageIcon: {
      fontSize: '1.5rem',
      lineHeight: 1,
    },
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: '0 0 2px 0',
    },
    pageSubtitle: {
      fontSize: '0.85rem',
      color: '#666',
      margin: 0,
    },
  };

  return (
    <div className="data-page">
      <div style={headerStyles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={headerStyles.pageIcon}>🤖</span>
          <div>
            <h1 style={headerStyles.pageTitle}>Agente IA WhatsApp</h1>
            <p style={headerStyles.pageSubtitle}>Configure o assistente virtual para atendimento automático via WhatsApp</p>
          </div>
        </div>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
          background: message.type === 'error' ? '#fef2f2' : '#f0fdf4',
          color: message.type === 'error' ? '#991b1b' : '#166534',
          border: `1px solid ${message.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="wa-agente-tabs" style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #3b82f6' : '3px solid transparent',
              background: 'none',
              color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === tab.id ? 600 : 400,
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '-2px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="gestao-loading"><div className="loading-spinner"></div><p>Carregando...</p></div>
      ) : (
        <>
          {/* ==================== TAB: CONFIG ==================== */}
          {activeTab === 'config' && (
            <div>
              {/* Toggle ON/OFF */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', padding: '16px', background: config.ai_enabled === '1' ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', border: `1px solid ${config.ai_enabled === '1' ? '#bbf7d0' : '#fecaca'}` }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.ai_enabled === '1'}
                    onChange={(e) => setConfig(prev => ({ ...prev, ai_enabled: e.target.checked ? '1' : '0' }))}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontWeight: 600, fontSize: '15px' }}>
                    Agente IA {config.ai_enabled === '1' ? 'Ativado' : 'Desativado'}
                  </span>
                </label>
              </div>

              {/* Nome do Agente */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>Nome do Agente IA</label>
                <input
                  type="text"
                  value={config.ai_nome_agente}
                  onChange={(e) => setConfig(prev => ({ ...prev, ai_nome_agente: e.target.value }))}
                  placeholder="Ex: Assistente Virtual Santa Rita"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', maxWidth: '500px' }}
                />
                <small style={{ color: '#9ca3af', fontSize: '11px', display: 'block', marginTop: '4px' }}>Nome exibido no chat para mensagens do agente IA</small>
              </div>

              <div className="wa-agente-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Coluna esquerda */}
                <div>
                  {/* API Key */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>API Key OpenRouter</label>
                    {hasApiKey && !editingApiKey ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="text" value={apiKeyMasked} disabled style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#f9fafb', fontSize: '13px' }} />
                        <button onClick={() => setEditingApiKey(true)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Alterar</button>
                      </div>
                    ) : (
                      <input
                        type="password"
                        value={config.openrouter_api_key}
                        onChange={(e) => { setConfig(prev => ({ ...prev, openrouter_api_key: e.target.value })); setEditingApiKey(true); }}
                        placeholder="sk-mdai-v1-..."
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                      />
                    )}
                    <small style={{ color: '#9ca3af', fontSize: '11px' }}>Chave do gateway (modelosdeia) — gerada em modelosdeia.rafaeljunior.vip, aba Gateway</small>
                  </div>

                  {/* Modelo selecionado */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>Modelo de IA</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#f9fafb', fontSize: '13px', minHeight: '36px' }}>
                        {selectedModel ? (
                          <span>
                            <strong>{selectedModel.provider}</strong> / {selectedModel.name}
                            {selectedModel.pricing.prompt_per_million > 0 && (
                              <span style={{ color: '#6b7280', marginLeft: '8px', fontSize: '12px' }}>
                                (${selectedModel.pricing.prompt_per_million.toFixed(2)} / ${selectedModel.pricing.completion_per_million.toFixed(2)} por 1M)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: '#6b7280' }}>{config.ai_model}</span>
                        )}
                      </div>
                      <button
                        onClick={() => { const next = showModelBrowser === 'ai_model' ? null : 'ai_model'; setShowModelBrowser(next); if (next && allModels.length === 0) fetchModels(); }}
                        style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: showModelBrowser === 'ai_model' ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#3b82f6' }}
                      >
                        {showModelBrowser === 'ai_model' ? 'Fechar' : 'Escolher Modelo'}
                      </button>
                    </div>
                  </div>

                  {/* Modelo de Mineracao */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>Modelo de Mineracao (KB Mining)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#f9fafb', fontSize: '13px', minHeight: '36px' }}>
                        {(() => { const m = allModels.find(x => x.id === config.ai_mining_model); return m ? (
                          <span>
                            <strong>{m.provider}</strong> / {m.name}
                            {m.pricing.prompt_per_million > 0 && (
                              <span style={{ color: '#6b7280', marginLeft: '8px', fontSize: '12px' }}>
                                (${m.pricing.prompt_per_million.toFixed(2)} / ${m.pricing.completion_per_million.toFixed(2)} por 1M)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: '#6b7280' }}>{config.ai_mining_model}</span>
                        ); })()}
                      </div>
                      <button
                        onClick={() => { const next = showModelBrowser === 'ai_mining_model' ? null : 'ai_mining_model'; setShowModelBrowser(next); if (next && allModels.length === 0) fetchModels(); }}
                        style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: showModelBrowser === 'ai_mining_model' ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#3b82f6' }}
                      >
                        {showModelBrowser === 'ai_mining_model' ? 'Fechar' : 'Escolher Modelo'}
                      </button>
                    </div>
                    <small style={{ color: '#9ca3af', fontSize: '11px' }}>Modelo usado na aba Mineracao IA. Modelos baratos sao recomendados.</small>
                  </div>

                  {/* Modelo Validador */}
                  <div style={{ marginBottom: '20px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', background: config.ai_validator_enabled === '1' ? '#fefce8' : '#f9fafb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>Validador de Respostas</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={config.ai_validator_enabled === '1'}
                          onChange={(e) => setConfig(prev => ({ ...prev, ai_validator_enabled: e.target.checked ? '1' : '0' }))}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '12px', color: config.ai_validator_enabled === '1' ? '#854d0e' : '#6b7280' }}>
                          {config.ai_validator_enabled === '1' ? 'Ativo' : 'Inativo'}
                        </span>
                      </label>
                    </div>
                    <small style={{ color: '#9ca3af', fontSize: '11px', display: 'block', marginBottom: '8px' }}>
                      Um segundo modelo revisa a resposta antes de enviar, verificando se as tools foram usadas corretamente.
                    </small>
                    {config.ai_validator_enabled === '1' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', fontSize: '13px', minHeight: '36px' }}>
                          {(() => { const m = allModels.find(x => x.id === config.ai_validator_model); return m ? (
                            <span>
                              <strong>{m.provider}</strong> / {m.name}
                              {m.pricing.prompt_per_million > 0 && (
                                <span style={{ color: '#6b7280', marginLeft: '8px', fontSize: '12px' }}>
                                  (${m.pricing.prompt_per_million.toFixed(2)} / ${m.pricing.completion_per_million.toFixed(2)} por 1M)
                                </span>
                              )}
                            </span>
                          ) : (
                            <span style={{ color: '#6b7280' }}>{config.ai_validator_model || 'Nenhum modelo selecionado'}</span>
                          ); })()}
                        </div>
                        <button
                          onClick={() => { const next = showModelBrowser === 'ai_validator_model' ? null : 'ai_validator_model'; setShowModelBrowser(next); if (next && allModels.length === 0) fetchModels(); }}
                          style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: showModelBrowser === 'ai_validator_model' ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#3b82f6' }}
                        >
                          {showModelBrowser === 'ai_validator_model' ? 'Fechar' : 'Escolher Modelo'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Botões Interativos (Quick Reply + Lista) */}
                  <div style={{ marginBottom: '20px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', background: config.ai_buttons_enabled === '1' ? '#eff6ff' : '#f9fafb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>Botoes Interativos</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={config.ai_buttons_enabled === '1'}
                          onChange={(e) => setConfig(prev => ({ ...prev, ai_buttons_enabled: e.target.checked ? '1' : '0' }))}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '12px', color: config.ai_buttons_enabled === '1' ? '#1d4ed8' : '#6b7280' }}>
                          {config.ai_buttons_enabled === '1' ? 'Ativo' : 'Inativo'}
                        </span>
                      </label>
                    </div>
                    <small style={{ color: '#6b7280', fontSize: '11px', display: 'block', marginBottom: '8px' }}>
                      Um modelo secundario analisa cada resposta da IA e sugere botoes de resposta rapida (max 3) ou lista de opcoes para o paciente. Pode retornar nenhum botao se nao fizer sentido.
                    </small>
                    {config.ai_buttons_enabled === '1' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', fontSize: '13px', minHeight: '36px' }}>
                          {(() => { const m = allModels.find(x => x.id === config.ai_buttons_model); return m ? (
                            <span>
                              <strong>{m.provider}</strong> / {m.name}
                              {m.pricing.prompt_per_million > 0 && (
                                <span style={{ color: '#6b7280', marginLeft: '8px', fontSize: '12px' }}>
                                  (${m.pricing.prompt_per_million.toFixed(2)} / ${m.pricing.completion_per_million.toFixed(2)} por 1M)
                                </span>
                              )}
                            </span>
                          ) : (
                            <span style={{ color: '#6b7280' }}>{config.ai_buttons_model || 'Mesmo modelo principal'}</span>
                          ); })()}
                        </div>
                        <button
                          onClick={() => { const next = showModelBrowser === 'ai_buttons_model' ? null : 'ai_buttons_model'; setShowModelBrowser(next); if (next && allModels.length === 0) fetchModels(); }}
                          style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: showModelBrowser === 'ai_buttons_model' ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#3b82f6' }}
                        >
                          {showModelBrowser === 'ai_buttons_model' ? 'Fechar' : 'Escolher Modelo'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Transcrição de Áudio/Imagem */}
                  <div style={{ marginBottom: '20px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', background: config.ai_transcription_enabled === '1' ? '#faf5ff' : '#f9fafb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>Transcricao de Audio/Imagem</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={config.ai_transcription_enabled === '1'}
                          onChange={(e) => setConfig(prev => ({ ...prev, ai_transcription_enabled: e.target.checked ? '1' : '0' }))}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '12px', color: config.ai_transcription_enabled === '1' ? '#7c3aed' : '#6b7280' }}>
                          {config.ai_transcription_enabled === '1' ? 'Ativo' : 'Inativo'}
                        </span>
                      </label>
                    </div>
                    <small style={{ color: '#6b7280', fontSize: '11px', display: 'block', marginBottom: '8px' }}>
                      Usa um modelo multimodal para transcrever audios e descrever imagens. A transcricao e enviada ao paciente e depois processada pela IA. Recomendado: Gemini Flash.
                    </small>
                    {config.ai_transcription_enabled === '1' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', fontSize: '13px', minHeight: '36px' }}>
                          {(() => { const m = allModels.find(x => x.id === config.ai_transcription_model); return m ? (
                            <span>
                              <strong>{m.provider}</strong> / {m.name}
                              {m.pricing.prompt_per_million > 0 && (
                                <span style={{ color: '#6b7280', marginLeft: '8px', fontSize: '12px' }}>
                                  (${m.pricing.prompt_per_million.toFixed(2)} / ${m.pricing.completion_per_million.toFixed(2)} por 1M)
                                </span>
                              )}
                            </span>
                          ) : (
                            <span style={{ color: '#6b7280' }}>{config.ai_transcription_model || 'Mesmo modelo principal'}</span>
                          ); })()}
                        </div>
                        <button
                          onClick={() => { const next = showModelBrowser === 'ai_transcription_model' ? null : 'ai_transcription_model'; setShowModelBrowser(next); if (next && allModels.length === 0) fetchModels(); }}
                          style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: showModelBrowser === 'ai_transcription_model' ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#3b82f6' }}
                        >
                          {showModelBrowser === 'ai_transcription_model' ? 'Fechar' : 'Escolher Modelo'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Atendimento Automático (Follow-up + Fechamento) */}
                  <div style={{ marginBottom: '20px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', background: config.ai_followup_enabled === '1' ? '#f0fdf4' : '#f9fafb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>Atendimento Automatico</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={config.ai_followup_enabled === '1'}
                          onChange={(e) => setConfig(prev => ({ ...prev, ai_followup_enabled: e.target.checked ? '1' : '0' }))}
                        />
                        <span style={{ fontSize: '12px', color: config.ai_followup_enabled === '1' ? '#166534' : '#6b7280' }}>
                          {config.ai_followup_enabled === '1' ? 'Ativo' : 'Inativo'}
                        </span>
                      </label>
                    </div>
                    <small style={{ color: '#6b7280', display: 'block', marginBottom: '8px' }}>
                      Follow-up automatico e fechamento por inatividade. Cada conversa recebe um protocolo unico.
                    </small>
                    {config.ai_followup_enabled === '1' && (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                          <div>
                            <label style={{ display: 'block', fontWeight: 500, marginBottom: '4px', fontSize: '12px', color: '#374151' }}>Timeout Follow-up (min)</label>
                            <input
                              type="number" min="5" max="60"
                              value={config.ai_followup_timeout_min}
                              onChange={(e) => setConfig(prev => ({ ...prev, ai_followup_timeout_min: e.target.value }))}
                              style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                            />
                            <small style={{ color: '#9ca3af', fontSize: '11px' }}>Tempo sem resposta para enviar follow-up</small>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontWeight: 500, marginBottom: '4px', fontSize: '12px', color: '#374151' }}>Timeout Fechamento (min)</label>
                            <input
                              type="number" min="5" max="60"
                              value={config.ai_close_timeout_min}
                              onChange={(e) => setConfig(prev => ({ ...prev, ai_close_timeout_min: e.target.value }))}
                              style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                            />
                            <small style={{ color: '#9ca3af', fontSize: '11px' }}>Tempo apos follow-up para encerrar</small>
                          </div>
                        </div>

                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                          <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '13px', color: '#374151' }}>Modelos de Mensagem</label>
                          <small style={{ color: '#6b7280', display: 'block', marginBottom: '10px', fontSize: '11px' }}>
                            Use {'{protocolo}'} para inserir o numero do protocolo automaticamente.
                          </small>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                            <div>
                              <label style={{ display: 'block', fontWeight: 500, marginBottom: '4px', fontSize: '12px', color: '#374151' }}>Abertura de Atendimento</label>
                              <textarea
                                value={config.ai_msg_abertura}
                                onChange={(e) => setConfig(prev => ({ ...prev, ai_msg_abertura: e.target.value }))}
                                rows={3}
                                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', resize: 'vertical', fontFamily: 'inherit' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontWeight: 500, marginBottom: '4px', fontSize: '12px', color: '#374151' }}>Follow-up (fallback se IA nao responder)</label>
                              <textarea
                                value={config.ai_msg_followup}
                                onChange={(e) => setConfig(prev => ({ ...prev, ai_msg_followup: e.target.value }))}
                                rows={3}
                                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', resize: 'vertical', fontFamily: 'inherit' }}
                              />
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                              <label style={{ display: 'block', fontWeight: 500, marginBottom: '4px', fontSize: '12px', color: '#374151' }}>Fechamento por Inatividade</label>
                              <textarea
                                value={config.ai_msg_fechamento_inatividade}
                                onChange={(e) => setConfig(prev => ({ ...prev, ai_msg_fechamento_inatividade: e.target.value }))}
                                rows={3}
                                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', resize: 'vertical', fontFamily: 'inherit' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontWeight: 500, marginBottom: '4px', fontSize: '12px', color: '#374151' }}>Fechamento pelo Operador</label>
                              <textarea
                                value={config.ai_msg_fechamento_operador}
                                onChange={(e) => setConfig(prev => ({ ...prev, ai_msg_fechamento_operador: e.target.value }))}
                                rows={3}
                                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', resize: 'vertical', fontFamily: 'inherit' }}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Temperature + Max Tokens */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>Temperatura: {config.ai_temperature}</label>
                      <input type="range" min="0" max="1" step="0.1" value={config.ai_temperature} onChange={(e) => setConfig(prev => ({ ...prev, ai_temperature: e.target.value }))} style={{ width: '100%' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af' }}>
                        <span>Preciso</span><span>Criativo</span>
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>Max Tokens: {config.ai_max_tokens}</label>
                      <input type="range" min="100" max="4000" step="100" value={config.ai_max_tokens} onChange={(e) => setConfig(prev => ({ ...prev, ai_max_tokens: e.target.value }))} style={{ width: '100%' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af' }}>
                        <span>100</span><span>4000</span>
                      </div>
                    </div>
                  </div>

                  {/* Horario */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>Horario Inicio</label>
                      <input type="time" value={config.ai_horario_inicio} onChange={(e) => setConfig(prev => ({ ...prev, ai_horario_inicio: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>Horario Fim</label>
                      <input type="time" value={config.ai_horario_fim} onChange={(e) => setConfig(prev => ({ ...prev, ai_horario_fim: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                  </div>

                  {/* Max turns */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>Max. Turnos por Conversa</label>
                    <input type="number" min="5" max="100" value={config.ai_max_turns_per_conversation} onChange={(e) => setConfig(prev => ({ ...prev, ai_max_turns_per_conversation: e.target.value }))} style={{ width: '120px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
                  </div>
                </div>

                {/* Coluna direita */}
                <div>
                  {/* System Prompt */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>System Prompt</label>
                    <textarea
                      value={config.ai_system_prompt}
                      onChange={(e) => setConfig(prev => ({ ...prev, ai_system_prompt: e.target.value }))}
                      rows={10}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', resize: 'vertical' }}
                    />
                  </div>

                  {/* Mensagem fora de horario */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px', color: '#374151' }}>Mensagem Fora de Horario</label>
                    <textarea
                      value={config.ai_mensagem_fora_horario}
                      onChange={(e) => setConfig(prev => ({ ...prev, ai_mensagem_fora_horario: e.target.value }))}
                      rows={3}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', resize: 'vertical' }}
                    />
                    <small style={{ color: '#9ca3af', fontSize: '11px' }}>Variaveis: {'{hora_inicio}'}, {'{hora_fim}'}</small>
                  </div>
                </div>
              </div>

              {/* Base de Conhecimento */}
              <div style={{ marginTop: '24px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                <div className="wa-agente-kb-header" style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '14px', color: '#374151' }}>Base de Conhecimento</span>
                    <small style={{ display: 'block', color: '#9ca3af', fontSize: '11px', marginTop: '2px' }}>Informacoes estruturadas que o agente IA usara para responder. Preencha as categorias relevantes.</small>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {knowledgeBase.length === 0 && (
                      <button
                        onClick={() => setKnowledgeBase(DEFAULT_KNOWLEDGE_BASE)}
                        style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#374151' }}
                      >
                        Carregar Categorias Padrao
                      </button>
                    )}
                    <button
                      onClick={() => setKnowledgeBase(prev => [...prev, { categoria: 'Nova Categoria', conteudo: '' }])}
                      style={{ padding: '6px 12px', border: '1px solid #3b82f6', borderRadius: '6px', background: '#eff6ff', cursor: 'pointer', fontSize: '12px', color: '#3b82f6', fontWeight: 500 }}
                    >
                      + Adicionar Categoria
                    </button>
                  </div>
                </div>

                <div style={{ padding: '12px' }}>
                  {knowledgeBase.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '13px' }}>
                      Nenhuma categoria adicionada. Clique em "Carregar Categorias Padrao" para comecar.
                    </div>
                  )}

                  {knowledgeBase.map((entry, idx) => {
                    const isExpanded = expandedCategories[idx] !== false;
                    return (
                      <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', marginBottom: '8px', overflow: 'hidden', background: '#fff' }}>
                        {/* Card header */}
                        <div
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
                            background: entry.conteudo?.trim() ? '#f0fdf4' : '#f9fafb',
                            cursor: 'pointer', userSelect: 'none',
                          }}
                          onClick={() => setExpandedCategories(prev => ({ ...prev, [idx]: !isExpanded }))}
                        >
                          <span style={{ fontSize: '12px', color: '#6b7280', width: '16px', textAlign: 'center' }}>
                            {isExpanded ? '\u25BC' : '\u25B6'}
                          </span>
                          {isExpanded ? (
                            <input
                              type="text"
                              value={entry.categoria}
                              onChange={(e) => {
                                const updated = [...knowledgeBase];
                                updated[idx] = { ...updated[idx], categoria: e.target.value };
                                setKnowledgeBase(updated);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{ flex: 1, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', fontWeight: 600, color: '#374151', background: '#fff' }}
                            />
                          ) : (
                            <span style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                              {entry.categoria}
                            </span>
                          )}
                          {entry.conteudo?.trim() && (
                            <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '10px', background: '#bbf7d0', color: '#166534', fontWeight: 600 }}>
                              Preenchido
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setKnowledgeBase(prev => prev.filter((_, i) => i !== idx));
                            }}
                            style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '16px', color: '#9ca3af', lineHeight: 1 }}
                            title="Remover categoria"
                          >
                            &times;
                          </button>
                        </div>

                        {/* Card body */}
                        {isExpanded && (
                          <div style={{ padding: '10px 12px', borderTop: '1px solid #e5e7eb' }}>
                            <textarea
                              value={entry.conteudo}
                              onChange={(e) => {
                                const updated = [...knowledgeBase];
                                updated[idx] = { ...updated[idx], conteudo: e.target.value };
                                setKnowledgeBase(updated);
                              }}
                              placeholder={`Insira informacoes sobre "${entry.categoria}"...`}
                              rows={4}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', resize: 'vertical', fontFamily: 'inherit' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                <button onClick={saveConfig} disabled={saving} style={{ padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Salvando...' : 'Salvar Configuracao'}
                </button>
                <button onClick={testConnection} disabled={testingConnection} style={{ padding: '10px 24px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: testingConnection ? 'not-allowed' : 'pointer', opacity: testingConnection ? 0.6 : 1 }}>
                  {testingConnection ? 'Testando...' : 'Testar Conexao'}
                </button>
              </div>

              {/* Model Browser (expansível) */}
              {showModelBrowser && allModels.length >= 0 && (
                <div style={{ marginTop: '24px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  <div className="wa-agente-model-browser-header" style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px', color: '#374151', whiteSpace: 'nowrap' }}>
                      {showModelBrowser === 'ai_mining_model' ? 'Escolher Modelo de Mineracao' : showModelBrowser === 'ai_validator_model' ? 'Escolher Modelo Validador' : 'Modelos OpenRouter'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>({filteredModels.length} de {allModels.length})</span>
                    <input
                      type="text"
                      value={modelsFilter}
                      onChange={(e) => setModelsFilter(e.target.value)}
                      placeholder="Filtrar por nome, provedor..."
                      style={{ flex: 1, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                    />
                    <button onClick={fetchModels} disabled={modelsLoading} style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '12px' }}>
                      {modelsLoading ? 'Carregando...' : 'Atualizar'}
                    </button>
                  </div>
                  <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table className="wa-agente-model-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', position: 'sticky', top: 0, zIndex: 1 }}>
                          <th onClick={() => handleModelSort('provider')} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', cursor: 'pointer', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Provedor{sortIcon('provider')}</th>
                          <th onClick={() => handleModelSort('name')} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', cursor: 'pointer', borderBottom: '1px solid #e5e7eb' }}>Modelo{sortIcon('name')}</th>
                          <th onClick={() => handleModelSort('context')} style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#374151', cursor: 'pointer', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Contexto{sortIcon('context')}</th>
                          <th onClick={() => handleModelSort('input')} style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#374151', cursor: 'pointer', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Input/1M{sortIcon('input')}</th>
                          <th onClick={() => handleModelSort('output')} style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#374151', cursor: 'pointer', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Output/1M{sortIcon('output')}</th>
                          <th onClick={() => handleModelSort('created')} style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#374151', cursor: 'pointer', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Data{sortIcon('created')}</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', width: '60px' }}>Usar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedModels.slice(0, 200).map(m => {
                          const targetField = showModelBrowser || 'ai_model';
                          const isActive = config[targetField] === m.id;
                          return (
                          <tr
                            key={m.id}
                            style={{
                              background: isActive ? '#eff6ff' : 'transparent',
                              borderBottom: '1px solid #f3f4f6',
                            }}
                            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f9fafb'; }}
                            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                          >
                            <td style={{ padding: '6px 12px', color: '#6b7280' }}>{m.provider}</td>
                            <td style={{ padding: '6px 12px' }}>
                              <div style={{ fontWeight: isActive ? 600 : 400, color: '#111' }}>{m.name}</div>
                              {m.description && <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px', lineHeight: '1.3', maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.description}>{m.description}</div>}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', color: '#6b7280', fontFamily: 'monospace' }}>{formatContext(m.context_length)}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', color: m.pricing.prompt_per_million === 0 ? '#10b981' : '#374151', fontFamily: 'monospace' }}>{formatPrice(m.pricing.prompt_per_million)}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', color: m.pricing.completion_per_million === 0 ? '#10b981' : '#374151', fontFamily: 'monospace' }}>{formatPrice(m.pricing.completion_per_million)}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'center', color: '#6b7280', fontSize: '11px', whiteSpace: 'nowrap' }}>{m.created ? new Date(m.created * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                              {isActive ? (
                                <span style={{ color: '#3b82f6', fontWeight: 600, fontSize: '11px' }}>Ativo</span>
                              ) : (
                                <button
                                  onClick={() => setConfig(prev => ({ ...prev, [targetField]: m.id }))}
                                  style={{ padding: '3px 10px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '11px', color: '#374151' }}
                                >
                                  Usar
                                </button>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {modelsLoading && <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>Carregando modelos...</div>}
                    {!modelsLoading && sortedModels.length === 0 && <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>Nenhum modelo encontrado</div>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== TAB: TOOLS ==================== */}
          {activeTab === 'tools' && (
            <div>
              <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '13px' }}>
                Selecione quais funcoes o agente IA pode usar para buscar dados do sistema. Desativar funcoes limita o que o agente consegue responder.
              </p>
              <div className="wa-agente-tools-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '12px' }}>
                {availableTools.map(tool => (
                  <div key={tool.name} style={{ padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: enabledToolsList.includes(tool.name) ? '#f0fdf4' : '#f9fafb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <input type="checkbox" checked={enabledToolsList.includes(tool.name)} onChange={() => toggleTool(tool.name)} style={{ width: '16px', height: '16px' }} />
                      <span style={{ fontWeight: 600, fontSize: '14px', fontFamily: 'monospace', color: '#374151' }}>{tool.name}</span>
                    </div>
                    <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 8px 28px' }}>{tool.description}</p>
                    {tool.parameters?.properties && Object.keys(tool.parameters.properties).length > 0 && (
                      <div style={{ marginLeft: '28px', fontSize: '12px', color: '#9ca3af' }}>
                        Parametros: {Object.entries(tool.parameters.properties).map(([k, v]) => `${k} (${v.type})`).join(', ')}
                      </div>
                    )}
                    {tool.name === 'enviar_resultado_pdf' && enabledToolsList.includes('enviar_resultado_pdf') && (
                      <div style={{ marginTop: '10px', marginLeft: '28px', padding: '10px 12px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                          <input type="checkbox"
                            checked={config.ai_pdf_usar_interno === '1'}
                            onChange={(e) => setConfig(prev => ({ ...prev, ai_pdf_usar_interno: e.target.checked ? '1' : '0' }))}
                            style={{ width: '15px', height: '15px' }}
                          />
                          Usar PDF interno quando WMI indisponivel
                        </label>
                        <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0 23px' }}>
                          {config.ai_pdf_usar_interno === '1'
                            ? 'Se o PDF WMI nao estiver disponivel, o agente gerara um PDF interno com os resultados e enviara ao paciente.'
                            : 'Se o PDF WMI falhar, o agente informara que o PDF nao esta disponivel.'}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={saveConfig} disabled={saving} style={{ marginTop: '16px', padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Salvando...' : 'Salvar Funcoes'}
              </button>
            </div>
          )}

          {/* ==================== TAB: WEBHOOKS ==================== */}
          {activeTab === 'webhooks' && (
            <div>
              {/* Filtros */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={webhookFilters.event_type}
                  onChange={(e) => setWebhookFilters(prev => ({ ...prev, event_type: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                >
                  <option value="">Todos os tipos</option>
                  <option value="message">Message</option>
                  <option value="button">Button</option>
                  <option value="status">Status</option>
                </select>
                <input
                  type="text"
                  value={webhookFilters.from_phone}
                  onChange={(e) => setWebhookFilters(prev => ({ ...prev, from_phone: e.target.value }))}
                  placeholder="Filtrar por telefone..."
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '200px' }}
                />
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>{webhooksTotal} registros | Auto-refresh 15s</span>
                <button onClick={fetchWebhooks} disabled={webhooksLoading} style={{ marginLeft: 'auto', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '12px' }}>
                  {webhooksLoading ? 'Carregando...' : 'Atualizar'}
                </button>
              </div>

              {/* Tabela */}
              <div className="wa-agente-webhooks-table" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Data</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Tipo</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Telefone</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Message ID</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', width: '60px' }}>JSON</th>
                    </tr>
                  </thead>
                  <tbody>
                    {webhooks.map(wh => (
                      <React.Fragment key={wh.id}>
                        <tr
                          style={{ borderBottom: expandedWebhook === wh.id ? 'none' : '1px solid #f3f4f6', cursor: 'pointer' }}
                          onClick={() => setExpandedWebhook(expandedWebhook === wh.id ? null : wh.id)}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ padding: '8px 12px', color: '#374151', whiteSpace: 'nowrap' }}>
                            {new Date(wh.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500,
                              background: wh.event_type === 'message' ? '#dcfce7' : wh.event_type === 'status' ? '#dbeafe' : wh.event_type === 'button' ? '#fef3c7' : '#f3f4f6',
                              color: wh.event_type === 'message' ? '#166534' : wh.event_type === 'status' ? '#1e40af' : wh.event_type === 'button' ? '#92400e' : '#374151',
                            }}>
                              {wh.event_type || 'unknown'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>{wh.from_phone || '-'}</td>
                          <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '11px', color: '#9ca3af', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wh.message_id || '-'}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <span style={{ fontSize: '12px', color: expandedWebhook === wh.id ? '#3b82f6' : '#9ca3af' }}>
                              {expandedWebhook === wh.id ? '\u25BC' : '\u25B6'}
                            </span>
                          </td>
                        </tr>
                        {expandedWebhook === wh.id && (
                          <tr>
                            <td colSpan={5} style={{ padding: '0 12px 12px 12px', background: '#f8fafc' }}>
                              <pre style={{
                                padding: '12px', background: '#1e293b', color: '#e2e8f0', borderRadius: '6px',
                                fontSize: '11px', fontFamily: 'monospace', overflow: 'auto', maxHeight: '400px',
                                whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
                              }}>
                                {JSON.stringify(wh.payload, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                {webhooksLoading && <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>Carregando webhooks...</div>}
                {!webhooksLoading && webhooks.length === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Nenhum webhook registrado ainda. Os payloads serao armazenados automaticamente quando mensagens forem recebidas.</div>
                )}
              </div>
            </div>
          )}

          {/* ==================== TAB: TESTE ==================== */}
          {activeTab === 'teste' && (
            <div>
              <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '13px' }}>
                Simule uma conversa com o agente IA. As mensagens aqui usam um telefone de teste.
              </p>
              <div ref={testChatRef} style={{ height: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px 8px 0 0', padding: '16px', background: '#f3f4f6' }}>
                {testMessages.length === 0 && (
                  <p style={{ textAlign: 'center', color: '#9ca3af', marginTop: '150px' }}>Envie uma mensagem para comecar</p>
                )}
                {testMessages.map((msg, i) => (
                  <div key={i} style={{ marginBottom: '12px', display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '80%',
                      padding: '10px 14px',
                      borderRadius: msg.role === 'user' ? '12px 12px 0 12px' : '12px 12px 12px 0',
                      background: msg.role === 'user' ? '#dcfce7' : msg.role === 'error' ? '#fef2f2' : '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}>
                      <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: msg.role === 'error' ? '#991b1b' : '#111' }}>{msg.content}</div>
                    </div>
                  </div>
                ))}
                {testSending && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ padding: '10px 14px', borderRadius: '12px 12px 12px 0', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                      <span style={{ color: '#9ca3af', fontSize: '13px' }}>Digitando...</span>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', borderRadius: '0 0 8px 8px', border: '1px solid #e5e7eb', borderTop: 'none', padding: '12px', background: '#fff' }}>
                <input
                  type="text"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendTestMessage()}
                  placeholder="Digite uma mensagem..."
                  disabled={testSending}
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '20px', fontSize: '13px', outline: 'none' }}
                />
                <button onClick={sendTestMessage} disabled={testSending || !testInput.trim()} style={{ padding: '8px 20px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '20px', fontWeight: 600, cursor: testSending ? 'not-allowed' : 'pointer', opacity: testSending || !testInput.trim() ? 0.5 : 1 }}>
                  Enviar
                </button>
              </div>
              <button onClick={() => setTestMessages([])} style={{ marginTop: '12px', padding: '6px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#6b7280' }}>
                Limpar Chat
              </button>
            </div>
          )}

          {/* ==================== TAB: MINERACAO ==================== */}
          {activeTab === 'mineracao' && (
            <div>
              <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '13px' }}>
                Extraia informacoes relevantes das conversas fechadas do WhatsApp (Chatwoot) para alimentar a Base de Conhecimento do agente IA.
                Apenas conversas resolvidas sao mineradas. Conversas ja processadas nao serao repetidas.
              </p>

              {/* Stats do inbox */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                <div style={{ padding: '16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#0369a1', textTransform: 'uppercase', fontWeight: 600 }}>Resolvidas</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#0c4a6e' }}>{miningStats?.total_resolved || '-'}</div>
                </div>
                <div style={{ padding: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#15803d', textTransform: 'uppercase', fontWeight: 600 }}>Uteis (3+ msgs)</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#14532d' }}>{miningStats?.conversas_uteis || '-'}</div>
                </div>
                <div style={{ padding: '16px', background: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#5b21b6', textTransform: 'uppercase', fontWeight: 600 }}>Ja Mineradas</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#4c1d95' }}>{miningStats?.total_mineradas ?? '-'}</div>
                </div>
                <div style={{ padding: '16px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#92400e', textTransform: 'uppercase', fontWeight: 600 }}>Pendentes</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#78350f' }}>
                    {miningStats ? Math.max(0, (parseInt(miningStats.conversas_uteis) || 0) - (parseInt(miningStats.total_mineradas) || 0)) : '-'}
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#fdf4ff', border: '1px solid #f0abfc', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#86198f', textTransform: 'uppercase', fontWeight: 600 }}>Periodo</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#581c87', marginTop: '4px' }}>
                    {miningStats?.primeira_conversa ? new Date(miningStats.primeira_conversa).toLocaleDateString('pt-BR') : '-'}
                    {' - '}
                    {miningStats?.ultima_atividade ? new Date(miningStats.ultima_atividade).toLocaleDateString('pt-BR') : '-'}
                  </div>
                </div>
              </div>

              {/* Controles */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Lote (por request a IA)</label>
                  <select value={miningBatchSize} onChange={e => setMiningBatchSize(parseInt(e.target.value))} disabled={mining}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}>
                    <option value={10}>10 conversas</option>
                    <option value={20}>20 conversas</option>
                    <option value={30}>30 conversas</option>
                    <option value={50}>50 conversas</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total a processar</label>
                  <select value={miningMaxConversas} onChange={e => setMiningMaxConversas(parseInt(e.target.value))} disabled={mining}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}>
                    <option value={50}>50 conversas</option>
                    <option value={100}>100 conversas</option>
                    <option value={200}>200 conversas</option>
                    <option value={300}>300 conversas</option>
                    <option value={500}>500 conversas</option>
                  </select>
                </div>
                <button onClick={startMining} disabled={mining}
                  style={{ padding: '8px 24px', background: mining ? '#9ca3af' : '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: mining ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                  {mining ? 'Minerando...' : 'Iniciar Mineracao'}
                </button>
                <button onClick={() => { fetchMiningStats(); fetchMiningHistory(); }}
                  style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#6b7280' }}>
                  Atualizar
                </button>
                {miningHistory && miningHistory.total_mineradas > 0 && !miningResults && (
                  <button onClick={loadSavedResults}
                    style={{ padding: '8px 16px', border: '1px solid #c4b5fd', borderRadius: '6px', background: '#f5f3ff', cursor: 'pointer', fontSize: '12px', color: '#5b21b6' }}>
                    Ver Resultados Salvos
                  </button>
                )}
              </div>

              {/* Barra de progresso */}
              {(mining || miningProgress > 0) && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Progresso</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#7c3aed' }}>{miningProgress}%</span>
                  </div>
                  <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${miningProgress}%`, background: 'linear-gradient(90deg, #7c3aed, #a78bfa)', borderRadius: '4px', transition: 'width 0.3s ease' }} />
                  </div>
                </div>
              )}

              {/* Log de progresso */}
              {miningLog.length > 0 && (
                <div style={{ marginBottom: '20px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', background: '#1e1b2e', fontFamily: 'monospace' }}>
                  {miningLog.map((log, i) => (
                    <div key={i} style={{
                      fontSize: '12px',
                      padding: '2px 0',
                      color: log.type === 'error' ? '#f87171' : log.type === 'warn' ? '#fbbf24' : log.type === 'done' ? '#34d399' : log.type === 'success' ? '#a78bfa' : '#94a3b8',
                    }}>
                      {log.type === 'done' ? '✓ ' : log.type === 'error' ? '✗ ' : log.type === 'success' ? '● ' : '→ '}{log.text}
                    </div>
                  ))}
                </div>
              )}

              {/* Resultados por categoria */}
              {miningResults && Object.keys(miningResults).length > 0 && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111' }}>Resultados Extraidos</h3>
                    <button onClick={importResults} disabled={miningImporting}
                      style={{ padding: '8px 20px', background: miningImporting ? '#9ca3af' : '#10b981', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: miningImporting ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                      {miningImporting ? 'Importando...' : 'Importar Tudo para Base de Conhecimento'}
                    </button>
                  </div>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {Object.entries(miningResults).map(([key, items]) => {
                      if (!Array.isArray(items) || items.length === 0) return null;
                      return (
                        <div key={key} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px', color: '#374151' }}>
                              {MINING_CATEGORY_LABELS[key] || key}
                            </span>
                            <span style={{ fontSize: '12px', color: '#9ca3af', background: '#f3f4f6', padding: '2px 8px', borderRadius: '10px' }}>
                              {items.length} {items.length === 1 ? 'item' : 'itens'}
                            </span>
                          </div>
                          <div style={{ padding: '12px 16px' }}>
                            {key === 'perguntas_frequentes' ? (
                              items.map((faq, idx) => (
                                <div key={idx} style={{ marginBottom: idx < items.length - 1 ? '12px' : 0, padding: '8px', background: '#f9fafb', borderRadius: '6px' }}>
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', marginBottom: '4px' }}>
                                    P: {typeof faq === 'object' ? faq.pergunta : faq}
                                  </div>
                                  {typeof faq === 'object' && faq.resposta && (
                                    <div style={{ fontSize: '13px', color: '#6b7280' }}>R: {faq.resposta}</div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {items.map((item, idx) => (
                                  <li key={idx} style={{ fontSize: '13px', color: '#4b5563', marginBottom: '4px' }}>{String(item)}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Historico de mineracao */}
              {miningHistory && miningHistory.batches.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111' }}>
                      Historico de Mineracao ({miningHistory.total_mineradas} conversas)
                    </h3>
                    <button onClick={() => { if (window.confirm('Limpar todo o historico? As conversas poderao ser mineradas novamente.')) resetMiningHistory(); }}
                      style={{ padding: '6px 14px', border: '1px solid #fca5a5', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#dc2626' }}>
                      Limpar Historico
                    </button>
                  </div>
                  <div className="wa-agente-mining-table" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Lote</th>
                          <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Conversas</th>
                          <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Msgs</th>
                          <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Data</th>
                          <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Acao</th>
                        </tr>
                      </thead>
                      <tbody>
                        {miningHistory.batches.map((batch, i) => (
                          <tr key={batch.batch_id} style={{ borderBottom: i < miningHistory.batches.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                            <td style={{ padding: '8px 16px', color: '#6b7280', fontFamily: 'monospace', fontSize: '11px' }}>
                              {batch.batch_id.replace('batch_', '').substring(0, 10)}...
                            </td>
                            <td style={{ padding: '8px 16px', textAlign: 'center', fontWeight: 600 }}>{batch.conversas}</td>
                            <td style={{ padding: '8px 16px', textAlign: 'center', color: '#6b7280' }}>{batch.total_msgs}</td>
                            <td style={{ padding: '8px 16px', textAlign: 'right', color: '#6b7280' }}>
                              {new Date(batch.data).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                              <button onClick={() => resetMiningHistory(batch.batch_id)}
                                style={{ padding: '3px 10px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '11px', color: '#6b7280' }}>
                                Remover
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Estado vazio */}
              {!mining && !miningResults && miningLog.length === 0 && (!miningHistory || miningHistory.batches.length === 0) && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
                  Configure os parametros acima e clique em "Iniciar Mineracao" para extrair informacoes das conversas reais do Chatwoot.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WhatsAppAgente;
